import type { Address } from "viem"
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { UserInfo } from "../../domain/user/Models/UserInfo"
import { DomainError } from "../../domain/_kernel/DomainError"
import { DomainEventDetail, DomainEvents } from "../../domain/_kernel/Events"
import { TransactionType } from "../../domain/ethereum/Models/Transaction"

interface UserInfoState {
  userInfo?: UserInfo
  isLoading: boolean
  error?: string
  _isListening: boolean
}

interface UserInfoActions {
  getOrFetch: (userId?: string) => Promise<UserInfo | undefined>
  updateUserInfo: (userId?: string) => Promise<UserInfo | undefined>
  createWallet: (address: Address) => Promise<UserInfo | undefined>
  setUserInfo: (userInfo: UserInfo) => void
  reset: () => void
  startListening: () => void
  stopListening: () => void
}

type UserInfoStore = UserInfoState & UserInfoActions

export const useUserInfoStore = create<UserInfoStore>()(
  devtools(
    (set, get) => {
      const handleSuccessTransaction = (e: CustomEvent<DomainEventDetail[DomainEvents.SUCCESS_TRANSACTION]>) => {
        if (
          e.detail.transaction.type === TransactionType.VOTE ||
          e.detail.transaction.type === TransactionType.VOTE_EDIT
        ) {
          get().updateUserInfo()
        }
      }

      return {
        userInfo: undefined,
        isLoading: false,
        error: undefined,
        _isListening: false,

        getOrFetch: async (userId?: string) => {
          const state = get()
          if (state.userInfo) return state.userInfo
          return state.updateUserInfo(userId)
        },

        updateUserInfo: async (userId?: string) => {
          const state = get()
          if (state.isLoading) return undefined

          set({ isLoading: true, error: undefined })

          try {
            const { userInfo } = await window.domain.GetUserInfoUserUseCase.execute({ userId })
            set({ userInfo, isLoading: false })
            return userInfo
          } catch (error) {
            const message = error instanceof DomainError
              ? error.error.message
              : error instanceof Error
                ? error.message
                : String(error)
            set({ error: message, isLoading: false })
            throw error
          }
        },

        createWallet: async (address: Address) => {
          await window.domain.CreateWalletUserUseCase.execute({ address })
          return get().updateUserInfo()
        },

        setUserInfo: (userInfo: UserInfo) => {
          set({ userInfo })
        },

        reset: () => {
          set({ userInfo: undefined, isLoading: false, error: undefined })
        },

        startListening: () => {
          if (get()._isListening) return
          set({ _isListening: true })
          window.addEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
        },

        stopListening: () => {
          if (!get()._isListening) return
          set({ _isListening: false })
          window.removeEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
        },
      }
    },
    { name: "UserInfoStore" },
  ),
)
